import { useAdvancedEditorContext } from '@/pages/AdvancedEditor/context/useAdvancedEditorContext';
import {
    PolotnoStoreActivePageKeys,
    usePolotnoActivePageState,
} from '@/pages/AdvancedEditor/hooks/polotno-store/usePolotnoActivePageState';
import { getAvatarPresenterFromPage } from '@/pages/AdvancedEditor/utils';
import styled from '@emotion/styled';
import { Tabs } from '@mui/material';
import { observer } from 'mobx-react-lite';
import { useEffect, useState } from 'react';
import { TabButton } from '../common/TabButton';
import { AvatarSection } from './AvatarSection';
import { DeleteAvatarModal } from './DeleteAvatarModal';
import { GeneratedSection } from './GeneratedSection';
import { ModifyPresenter } from './ModifyPresenter';

const AvatarPanelContainer = styled.div`
    padding: 0 20px;
    height: 100%;
    display: flex;
    flex-direction: column;
    gap: 16px;
`;

const AvatarList = styled.div<{ disabled: boolean }>`
    height: 100%;
    display: flex;
    flex-direction: column;
    gap: 16px;
    pointer-events: ${({ disabled }) => (disabled ? 'none' : 'auto')};

    :after {
        content: '';
        position: absolute;
        top: 0;
        left: 0;
        width: 100%;
        height: 100%;
        background-color: white;
        opacity: ${({ disabled }) => (disabled ? 0.5 : 0)};
        z-index: ${({ disabled }) => (disabled ? 1000 : -1)};
        pointer-events: ${({ disabled }) => (disabled ? 'none' : 'auto')};
        transition: opacity 0.3s ease;
    }
`;

export const AvatarsPanel = observer(() => {
    const { store, editorInitialized } = useAdvancedEditorContext();
    const currentAvatar = getAvatarPresenterFromPage(store.activePage);
    const [isAddingAvatar, setIsAddingAvatar] = usePolotnoActivePageState(PolotnoStoreActivePageKeys.isAddingAvatar);

    const [currentTab, setCurrentTab] = useState<'avatar' | 'generated'>('avatar');
    const [editMode, setEditMode] = useState<boolean>(!!currentAvatar);

    useEffect(() => {
        setEditMode(!!currentAvatar);

        // add delay to prevent flickering
        if (editorInitialized) {
            setTimeout(() => {
                setIsAddingAvatar(false);
            }, 200);
        }
    }, [currentAvatar, editorInitialized]);

    const handleChange = (_: any, tab: 'avatar' | 'generated') => {
        setCurrentTab(tab);
    };

    return (
        <AvatarPanelContainer data-testid="avatar-panel">
            {!editMode && (
                <AvatarList disabled={isAddingAvatar}>
                    <Tabs value={currentTab} onChange={handleChange}>
                        <TabButton data-testid="avatar-tab-not-ai-generated" value="avatar" label="Avatar" />
                        <TabButton data-testid="avatar-tab-ai-generated" value="generated" label="AI Generated" />
                    </Tabs>

                    {currentTab === 'avatar' && <AvatarSection setEditMode={setEditMode} />}
                    {currentTab === 'generated' && <GeneratedSection setEditMode={setEditMode} />}
                </AvatarList>
            )}

            {editMode && <ModifyPresenter setEditMode={setEditMode} />}

            <DeleteAvatarModal />
        </AvatarPanelContainer>
    );
});
